/**
 * Opportunity Service
 *
 * Handles all opportunity pipeline operations.
 *
 * @module services/opportunityService
 */

import { supabase } from './supabase';
import { terminoParaFiltro, columnaNormalizada } from '../utils/normalizacionBusqueda';

/**
 * Default probability by status
 */
export const STATUS_PROBABILITY = {
  lead: 10,
  prospect: 20,
  qualified: 40,
  proposal: 60,
  negotiation: 75,
  won: 100,
  lost: 0,
};

/**
 * List opportunities with optional filters
 * @param {Object} filters - Filter options
 * @param {string} filters.status - Filter by status
 * @param {string} filters.clientId - Filter by client
 * @param {string} filters.assignedTo - Filter by assigned user
 * @param {string} filters.search - Search term
 * @param {number} filters.page - Page number (0-indexed)
 * @param {number} filters.pageSize - Items per page
 * @returns {Promise<{data, count, error}>}
 */
export const listOpportunities = async (filters = {}) => {
  try {
    let query = supabase
      .from('opportunities')
      .select(`
        *,
        client:clients(id, company_name),
        assigned:profiles!opportunities_assigned_to_fkey(id, full_name),
        project:projects(id, project_number)
      `, { count: 'exact' });

    if (filters.status) {
      query = query.eq('status', filters.status);
    }

    if (filters.clientId) {
      query = query.eq('client_id', filters.clientId);
    }

    if (filters.assignedTo) {
      query = query.eq('assigned_to', filters.assignedTo);
    }

    if (filters.search) {
      // #739/#742 — término del módulo compartido; el texto humano va contra su
      // columna generada y los códigos y emails quedan crudos.
      query = query.ilike(columnaNormalizada('title'), `%${terminoParaFiltro(filters.search)}%`);
    }

    // Pagination
    const page = filters.page || 0;
    const pageSize = filters.pageSize || 20;
    const from = page * pageSize;
    const to = from + pageSize - 1;

    query = query
      .range(from, to)
      .order('created_at', { ascending: false });

    const { data, count, error } = await query;

    if (error) throw error;

    return { data, count, error: null };
  } catch (error) {
    console.error('List opportunities error:', error);
    return { data: null, count: 0, error };
  }
};

/**
 * Get a single opportunity by ID
 * @param {string} opportunityId - Opportunity ID
 * @returns {Promise<{data, error}>}
 */
export const getOpportunity = async (opportunityId) => {
  try {
    const { data, error } = await supabase
      .from('opportunities')
      .select(`
        *,
        client:clients(id, company_name, primary_contact_email, primary_contact_phone),
        assigned:profiles!opportunities_assigned_to_fkey(id, full_name),
        project:projects(id, project_number, title),
        quotation:quotations(id, quotation_number, total, status)
      `)
      .eq('id', opportunityId)
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Get opportunity error:', error);
    return { data: null, error };
  }
};

/**
 * Create a new opportunity
 * @param {Object} opportunityData - Opportunity data
 * @returns {Promise<{data, error}>}
 */
export const createOpportunity = async (opportunityData) => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    const { data, error } = await supabase
      .from('opportunities')
      .insert({
        ...opportunityData,
        created_by: user.id,
      })
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Create opportunity error:', error);
    return { data: null, error };
  }
};

/**
 * Update an opportunity
 * @param {string} opportunityId - Opportunity ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<{data, error}>}
 */
export const updateOpportunity = async (opportunityId, updates) => {
  try {
    const { data, error } = await supabase
      .from('opportunities')
      .update(updates)
      .eq('id', opportunityId)
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Update opportunity error:', error);
    return { data: null, error };
  }
};

/**
 * Delete an opportunity
 * @param {string} opportunityId - Opportunity ID
 * @returns {Promise<{success, error}>}
 */
export const deleteOpportunity = async (opportunityId) => {
  try {
    const { error } = await supabase
      .from('opportunities')
      .delete()
      .eq('id', opportunityId);

    if (error) throw error;

    return { success: true, error: null };
  } catch (error) {
    console.error('Delete opportunity error:', error);
    return { success: false, error };
  }
};

/**
 * Convert a won opportunity to a project
 * @param {string} opportunityId - Opportunity ID
 * @param {Object} projectData - Additional project data
 * @returns {Promise<{data, error}>}
 */
export const convertToProject = async (opportunityId, projectData = {}) => {
  try {
    const { data: opportunity, error: oppError } = await getOpportunity(opportunityId);
    if (oppError) throw oppError;

    if (opportunity.status !== 'won') {
      throw new Error('Solo se pueden convertir oportunidades ganadas');
    }

    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    // Create the project
    // client_id es la unica fuente de verdad (FK a public.clients desde la
    // migracion 021). El espejo a `client_id_new` se quito en #338: esa columna
    // es el resto de una estrategia de migracion abandonada, y escribirla
    // mantenia viva la segunda FK que rompia los embeds de PostgREST.
    // Escribir el espejo era un no-op -- siempre llevaba el MISMO valor.
    const { data: project, error: projectError } = await supabase
      .from('projects')
      .insert({
        title: projectData.title || opportunity.title,
        description: projectData.description || opportunity.description,
        client_id: opportunity.client_id,
        quoted_amount: opportunity.estimated_value,
        status: 'draft',
        created_by: user.id,
      })
      .select()
      .single();

    if (projectError) throw projectError;

    // Link opportunity to project
    const { error: updateError } = await supabase
      .from('opportunities')
      .update({ project_id: project.id })
      .eq('id', opportunityId);

    if (updateError) throw updateError;

    return { data: project, error: null };
  } catch (error) {
    console.error('Convert to project error:', error);
    return { data: null, error };
  }
};
